import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useRecruitment } from '../../context/RecruitmentContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { 
  Plus, Search, MoreHorizontal, Pencil, Trash2, Eye, Upload, Download,
  FileText, User, Mail, Phone, Building, Calendar, Briefcase, Tag,
  Loader2, X, CheckCircle, AlertCircle, UserPlus, MapPin, ExternalLink, Clock
} from 'lucide-react';

const statusColors = {
  'New': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-yellow-100 text-yellow-700',
  'On Hold': 'bg-gray-100 text-gray-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Offer Extended': 'bg-purple-100 text-purple-700',
  'Offer Accepted': 'bg-green-100 text-green-700',
  'Offer Rejected': 'bg-red-100 text-red-700',
  'Joined': 'bg-emerald-100 text-emerald-700',
  'Converted': 'bg-teal-100 text-teal-700',
  'Withdrawn': 'bg-slate-100 text-slate-700'
};

const CandidateList = ({ onViewCandidate, initialSearch, getCandidateUrl }) => {
  const { 
    candidates, loading, jobs, employees, pipelineTemplates,
    fetchCandidates, createCandidate, updateCandidate, deleteCandidate,
    fetchJobs, fetchEmployeesLookup, parseResume,
    getSources, convertToEmployee, fetchTags
  } = useRecruitment();

  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  // True until the candidate filter effect has fetched once. See that effect.
  const firstFilterRun = useRef(true);

  const [searchQuery, setSearchQuery] = useState(initialSearch || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);

  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [sources, setSources] = useState([]);
  const [parsedData, setParsedData] = useState(null);
  const [bulkResults, setBulkResults] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    current_company: '',
    years_of_experience: '',
    skills: [],
    current_ctc: '',
    expected_ctc: '',
    notice_period: '',
    source: '',
    referred_by: '',
    tags: [],
    job_id: '',
    notes: '',
    resume_file_id: '',
    resume_filename: ''
  });

  const [skillInput, setSkillInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Update search when initialSearch prop changes
  useEffect(() => {
    if (initialSearch !== undefined && initialSearch !== null && initialSearch !== searchQuery) {
      setSearchQuery(initialSearch);
    }
  }, [initialSearch]);

  // Candidates are NOT fetched here — the filter effect below does it, and
  // fires on mount with the same filters (searchQuery is initialised from
  // initialSearch). Fetching here as well meant two identical requests on
  // every open. The one thing this call did better was skip the 300ms
  // debounce; the filter effect now skips it on its first run instead.
  const loadInitialData = async () => {
    await fetchJobs();
    await fetchEmployeesLookup();
    const sourcesData = await getSources();
    setSources(sourcesData);
    const tagsData = await fetchTags();
    setAllTags(tagsData);
  };

  useEffect(() => {
    const filters = {};
    if (searchQuery) filters.search = searchQuery;
    if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;
    if (sourceFilter && sourceFilter !== 'all') filters.source = sourceFilter;
    if (jobFilter && jobFilter !== 'all') filters.job_id = jobFilter;
    if (tagFilter && tagFilter !== 'all') filters.tag = tagFilter;

    // Debounce typing, but not the very first load: there is nothing to
    // coalesce on mount, and waiting 300ms there would just delay the list.
    // The flag is cleared INSIDE the callback, i.e. only once a fetch has
    // actually fired — so React StrictMode's dev-only mount/unmount/remount
    // (which clears the first timer before it runs) still gets an immediate
    // load on the second mount rather than a debounced one.
    const delay = firstFilterRun.current ? 0 : 300;
    const timer = setTimeout(() => {
      firstFilterRun.current = false;
      fetchCandidates(filters);
    }, delay);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, sourceFilter, jobFilter, tagFilter]);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      location: '',
      current_company: '',
      years_of_experience: '',
      skills: [],
      current_ctc: '',
      expected_ctc: '',
      notice_period: '',
      source: '',
      referred_by: '',
      tags: [],
      job_id: '',
      notes: '',
      resume_file_id: '',
      resume_filename: ''
    });
    setSkillInput('');
    setTagInput('');
    setParsedData(null);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Name and Email are required');
      return;
    }

    setFormLoading(true);
    const result = await createCandidate({
      ...formData,
      years_of_experience: formData.years_of_experience ? parseFloat(formData.years_of_experience) : null,
      current_ctc: formData.current_ctc ? parseFloat(formData.current_ctc) : null,
      expected_ctc: formData.expected_ctc ? parseFloat(formData.expected_ctc) : null,
      job_id: formData.job_id || null,
      referred_by: formData.referred_by || null,
      resume_file_id: formData.resume_file_id || null,
      resume_filename: formData.resume_filename || null,
      location: formData.location || null
    });
    setFormLoading(false);

    if (result.success) {
      toast.success('Candidate created successfully');
      setCreateDialogOpen(false);
      resetForm();
    } else {
      toast.error(result.error);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Name and Email are required');
      return;
    }

    setFormLoading(true);
    const result = await updateCandidate(selectedCandidate.id, {
      ...formData,
      years_of_experience: formData.years_of_experience ? parseFloat(formData.years_of_experience) : null,
      current_ctc: formData.current_ctc ? parseFloat(formData.current_ctc) : null,
      expected_ctc: formData.expected_ctc ? parseFloat(formData.expected_ctc) : null
    });
    setFormLoading(false);

    if (result.success) {
      toast.success('Candidate updated successfully');
      setEditDialogOpen(false);
      resetForm();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async () => {
    setFormLoading(true);
    const result = await deleteCandidate(selectedCandidate.id);
    setFormLoading(false);

    if (result.success) {
      toast.success('Candidate deleted successfully');
      setDeleteDialogOpen(false);
    } else {
      toast.error(result.error);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormLoading(true);
    const result = await parseResume(file, formData.job_id || null);
    setFormLoading(false);

    if (result.success) {
      const parsed = result.data.parsed_data;
      
      // Check if AI parsing returned an error
      if (parsed?.error) {
        toast.error(parsed.error);
        // Still set the resume file reference even if parsing failed
        setFormData(prev => ({
          ...prev,
          resume_file_id: result.data.resume_file_id || '',
          resume_filename: result.data.resume_filename || ''
        }));
        setParsedData(null);
        return;
      }
      
      setParsedData(parsed);
      
      // Auto-fill form with parsed data including resume file reference
      setFormData(prev => ({
        ...prev,
        name: parsed.name || prev.name,
        email: parsed.email || prev.email,
        phone: parsed.phone || prev.phone,
        current_company: parsed.current_company || prev.current_company,
        years_of_experience: parsed.years_of_experience?.toString() || prev.years_of_experience,
        skills: parsed.skills || prev.skills,
        resume_file_id: result.data.resume_file_id || '',
        resume_filename: result.data.resume_filename || ''
      }));

      if (result.data.duplicate) {
        toast.warning(`Duplicate found: ${result.data.duplicate.name} (${result.data.duplicate.email})`);
      } else {
        toast.success('Resume parsed successfully');
      }
    } else {
      toast.error(result.error || 'Failed to parse resume');
    }

    e.target.value = '';
  };

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Initialize results with pending status for all files
    const initialResults = files.map(f => ({
      filename: f.name,
      status: 'pending',
      parsed_data: null,
      candidate_id: null,
      error: null
    }));
    setBulkResults(initialResults);
    setUploadDialogOpen(true);
    setBulkProcessing(true);

    const jobId = jobFilter !== 'all' ? jobFilter : null;

    // Process files one by one with live status updates
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Update status to "uploading"
      setBulkResults(prev => prev.map((r, idx) =>
        idx === i ? { ...r, status: 'uploading' } : r
      ));

      try {
        // Parse resume via optimized endpoint
        const result = await parseResume(file, jobId);

        if (!result.success) {
          setBulkResults(prev => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'error', error: result.error || 'Failed to parse' } : r
          ));
          continue;
        }

        const parsed = result.data?.parsed_data || {};
        const resumeFileId = result.data?.resume_file_id || null;
        const resumeFilename = result.data?.resume_filename || file.name;

        if (parsed.error) {
          setBulkResults(prev => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'error', parsed_data: parsed, error: parsed.error } : r
          ));
          continue;
        }

        // Check duplicate
        if (result.data?.duplicate) {
          setBulkResults(prev => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'duplicate', parsed_data: parsed, error: `Duplicate: ${result.data.duplicate.name} (${result.data.duplicate.email})`, candidate_id: result.data.duplicate.id } : r
          ));
          continue;
        }

        // Auto-create candidate
        setBulkResults(prev => prev.map((r, idx) =>
          idx === i ? { ...r, status: 'creating' } : r
        ));

        const skills = Array.isArray(parsed.skills) ? parsed.skills : 
          (typeof parsed.skills === 'string' ? parsed.skills.split(',').map(s => s.trim()).filter(Boolean) : []);

        const createResult = await createCandidate({
          name: parsed.name || 'Unknown',
          email: parsed.email || `unknown-${Date.now()}@placeholder.com`,
          phone: parsed.phone || null,
          location: parsed.location || null,
          current_company: parsed.current_company || null,
          years_of_experience: parsed.years_of_experience || null,
          skills: skills,
          tags: [],
          job_id: jobId,
          resume_file_id: resumeFileId,
          resume_filename: resumeFilename,
          source: 'Bulk Upload'
        });

        if (createResult.success) {
          setBulkResults(prev => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'done', parsed_data: parsed, candidate_id: createResult.data?.id } : r
          ));
        } else {
          setBulkResults(prev => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'error', parsed_data: parsed, error: createResult.error || 'Failed to create candidate' } : r
          ));
        }
      } catch (err) {
        setBulkResults(prev => prev.map((r, idx) =>
          idx === i ? { ...r, status: 'error', error: err.message || 'Unexpected error' } : r
        ));
      }
    }

    setBulkProcessing(false);
    // Refresh candidates list
    await fetchCandidates();
    e.target.value = '';
  };

  const openCandidateNewWindow = (candidateId) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('tab', 'candidates');
    currentUrl.searchParams.set('candidate_id', candidateId);
    window.open(currentUrl.toString(), '_blank');
  };

  const handleConvertToEmployee = async () => {
    setFormLoading(true);
    const result = await convertToEmployee(selectedCandidate.id);
    setFormLoading(false);

    if (result.success) {
      toast.success('Candidate converted to employee successfully');
      setConvertDialogOpen(false);
      
      // Optionally redirect to Asset Manager (it signs in from the session)
      if (result.data.redirect_to) {
        window.open(result.data.redirect_to, '_blank');
      }
    } else {
      toast.error(result.error);
    }
  };

  const openEdit = (candidate) => {
    setSelectedCandidate(candidate);
    setFormData({
      name: candidate.name || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      location: candidate.location || '',
      current_company: candidate.current_company || '',
      years_of_experience: candidate.years_of_experience?.toString() || '',
      skills: candidate.skills || [],
      current_ctc: candidate.current_ctc?.toString() || '',
      expected_ctc: candidate.expected_ctc?.toString() || '',
      notice_period: candidate.notice_period || '',
      source: candidate.source || '',
      referred_by: candidate.referred_by || '',
      tags: candidate.tags || [],
      job_id: candidate.job_id || '',
      notes: candidate.notes || ''
    });
    setEditDialogOpen(true);
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData(prev => ({ ...prev, skills: [...prev.skills, skillInput.trim()] }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addTag = (tagValue) => {
    const tag = (tagValue || tagInput).trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      if (!allTags.includes(tag)) {
        setAllTags(prev => [...prev, tag].sort());
      }
    }
    setTagInput('');
    setShowTagSuggestions(false);
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const filteredTagSuggestions = tagInput.trim().length > 0
    ? allTags.filter(t => t.toLowerCase().includes(tagInput.toLowerCase()) && !formData.tags.includes(t))
    : [];

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setSourceFilter('all');
    setJobFilter('all');
    setTagFilter('all');
  };

  const candidateFormContent = useMemo(() => {
    const isEdit = editDialogOpen;
    return (
    <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto px-1">
      {/* Resume Upload */}
      {!isEdit && (
        <div className="space-y-2 p-4 bg-purple-50 rounded-lg border border-purple-100">
          <Label className="text-purple-700 font-medium">AI Resume Parser</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={formLoading}
              className="gap-2"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload Resume (PDF)
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleResumeUpload}
            />
          </div>
          {parsedData && (
            <div className="text-xs text-purple-600 space-y-1">
              <p className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Resume parsed - fields auto-filled
              </p>
              {formData.resume_filename && (
                <p className="flex items-center gap-1 text-green-600">
                  <FileText className="w-3 h-3" />
                  Resume attached: {formData.resume_filename}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Full name"
            data-testid="candidate-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="email@example.com"
            data-testid="candidate-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="+91 9999999999"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="location">Location / City</Label>
          <Input
            id="location"
            value={formData.location}
            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
            placeholder="e.g. Mumbai, Bangalore"
            data-testid="candidate-location"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="current_company">Current Company</Label>
          <Input
            id="current_company"
            value={formData.current_company}
            onChange={(e) => setFormData(prev => ({ ...prev, current_company: e.target.value }))}
            placeholder="Company name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="years_of_experience">Experience (Years)</Label>
          <Input
            id="years_of_experience"
            type="number"
            value={formData.years_of_experience}
            onChange={(e) => setFormData(prev => ({ ...prev, years_of_experience: e.target.value }))}
            placeholder="0"
          />
        </div>
        <div className="space-y-2">
          <Label>Notice Period</Label>
          <Select 
            value={formData.notice_period || 'not_specified'} 
            onValueChange={(v) => setFormData(prev => ({ ...prev, notice_period: v === 'not_specified' ? '' : v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not_specified">Not specified</SelectItem>
              <SelectItem value="Immediate">Immediate</SelectItem>
              <SelectItem value="15 days">15 days</SelectItem>
              <SelectItem value="30 days">30 days</SelectItem>
              <SelectItem value="60 days">60 days</SelectItem>
              <SelectItem value="90 days">90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="current_ctc">Current CTC (LPA)</Label>
          <Input
            id="current_ctc"
            type="number"
            value={formData.current_ctc}
            onChange={(e) => setFormData(prev => ({ ...prev, current_ctc: e.target.value }))}
            placeholder="0"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="expected_ctc">Expected CTC (LPA)</Label>
          <Input
            id="expected_ctc"
            type="number"
            value={formData.expected_ctc}
            onChange={(e) => setFormData(prev => ({ ...prev, expected_ctc: e.target.value }))}
            placeholder="0"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Source</Label>
          <Select 
            value={formData.source || 'not_specified'} 
            onValueChange={(v) => setFormData(prev => ({ ...prev, source: v === 'not_specified' ? '' : v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select source..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not_specified">Not specified</SelectItem>
              <SelectItem value="LinkedIn">LinkedIn</SelectItem>
              <SelectItem value="Naukri">Naukri</SelectItem>
              <SelectItem value="Referral">Referral</SelectItem>
              <SelectItem value="Campus">Campus</SelectItem>
              <SelectItem value="Direct">Direct</SelectItem>
              <SelectItem value="Agency">Agency</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Referred By</Label>
          <Select 
            value={formData.referred_by || 'none'} 
            onValueChange={(v) => setFormData(prev => ({ ...prev, referred_by: v === 'none' ? '' : v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select employee..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {employees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.employee_name} ({emp.employee_id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Job Opening</Label>
        <Select 
          value={formData.job_id || 'none'} 
          onValueChange={(v) => setFormData(prev => ({ ...prev, job_id: v === 'none' ? '' : v }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select job..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No specific job</SelectItem>
            {jobs.filter(j => j.status === 'Open').map((job) => (
              <SelectItem key={job.id} value={job.id}>
                {job.title} {job.department && `(${job.department})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Skills</Label>
        <div className="flex gap-2">
          <Input
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            placeholder="Add a skill..."
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
          />
          <Button type="button" variant="outline" onClick={addSkill}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {formData.skills?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {formData.skills.map((skill, idx) => (
              <Badge key={idx} variant="secondary" className="gap-1">
                {skill}
                <X className="w-3 h-3 cursor-pointer" onClick={() => removeSkill(skill)} />
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>Tags</Label>
        <div className="relative">
          <div className="flex gap-2">
            <Input
              value={tagInput}
              onChange={(e) => { setTagInput(e.target.value); setShowTagSuggestions(true); }}
              placeholder="Add a tag..."
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              onFocus={() => setShowTagSuggestions(true)}
              onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
              data-testid="tag-input"
            />
            <Button type="button" variant="outline" onClick={() => addTag()}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {showTagSuggestions && filteredTagSuggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-32 overflow-y-auto" data-testid="tag-suggestions">
              {filteredTagSuggestions.map((tag, idx) => (
                <div
                  key={idx}
                  className="px-3 py-1.5 text-sm cursor-pointer hover:bg-purple-50 hover:text-purple-700"
                  onMouseDown={(e) => { e.preventDefault(); addTag(tag); }}
                >
                  {tag}
                </div>
              ))}
            </div>
          )}
        </div>
        {formData.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {formData.tags.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="gap-1 bg-purple-50 text-purple-700 border-purple-200">
                <Tag className="w-3 h-3" />
                {tag}
                <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Additional notes..."
          rows={3}
        />
      </div>
    </div>
  );
  }, [formData, formLoading, parsedData, skillInput, tagInput, employees, jobs, editDialogOpen]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Candidates</h2>
          <p className="text-slate-500">Manage your talent pool</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} className="gap-2 bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4" />
            Add Candidate
          </Button>
          <Button variant="outline" onClick={() => bulkFileInputRef.current?.click()} className="gap-2">
            <Upload className="w-4 h-4" />
            Bulk Upload
          </Button>
          <input
            ref={bulkFileInputRef}
            type="file"
            accept=".pdf"
            multiple
            className="hidden"
            onChange={handleBulkUpload}
          />
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-muted-foreground text-sm">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email, skills, or tags..."
                  className="pl-10"
                  data-testid="candidate-search"
                />
              </div>
            </div>
            
            <div className="w-[150px]">
              <Label className="text-muted-foreground text-sm">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Offer Extended">Offer Extended</SelectItem>
                  <SelectItem value="Offer Accepted">Offer Accepted</SelectItem>
                  <SelectItem value="Joined">Joined</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-[150px]">
              <Label className="text-muted-foreground text-sm">Source</Label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {sources.map(src => (
                    <SelectItem key={src} value={src}>{src}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-[180px]">
              <Label className="text-muted-foreground text-sm">Job</Label>
              <Select value={jobFilter} onValueChange={setJobFilter}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Jobs</SelectItem>
                  {jobs.map(job => (
                    <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-[170px]">
              <Label className="text-muted-foreground text-sm">Tag</Label>
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="mt-1" data-testid="tag-filter-dropdown">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {allTags.map(tag => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(searchQuery || statusFilter !== 'all' || sourceFilter !== 'all' || jobFilter !== 'all' || tagFilter !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                <X className="w-4 h-4" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Candidates Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Candidates</span>
            <Badge variant="secondary">{candidates?.length || 0} records</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" />
                    </TableCell>
                  </TableRow>
                ) : candidates?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No candidates found
                    </TableCell>
                  </TableRow>
                ) : (
                  candidates?.map(candidate => (
                    <TableRow key={candidate.id}>
                      <TableCell>
                        <a
                          href={getCandidateUrl ? getCandidateUrl(candidate.id) : '#'}
                          onClick={(e) => {
                            // Left-click: in-place navigation. Cmd/Ctrl/middle-click: browser handles new tab.
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                            e.preventDefault();
                            onViewCandidate?.(candidate.id);
                          }}
                          className="cursor-pointer hover:text-purple-600 inline-block"
                          data-testid={`candidate-link-${candidate.id}`}
                        >
                          <p className="font-medium hover:underline">{candidate.name}</p>
                          {candidate.current_company && (
                            <p className="text-xs text-muted-foreground">{candidate.current_company}</p>
                          )}
                        </a>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{candidate.email}</p>
                          {candidate.phone && <p className="text-muted-foreground">{candidate.phone}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{candidate.location || '-'}</span>
                      </TableCell>
                      <TableCell>
                        {candidate.years_of_experience ? `${candidate.years_of_experience} yrs` : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {candidate.tags?.length > 0 ? candidate.tags.slice(0, 3).map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-200">
                              {tag}
                            </Badge>
                          )) : <span className="text-muted-foreground text-sm">-</span>}
                          {candidate.tags?.length > 3 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              +{candidate.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[candidate.status] || 'bg-gray-100'}>
                          {candidate.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{candidate.source || '-'}</TableCell>
                      <TableCell>
                        {jobs.find(j => j.id === candidate.job_id)?.title || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onViewCandidate?.(candidate.id)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEdit(candidate)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {candidate.status === 'Joined' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => { setSelectedCandidate(candidate); setConvertDialogOpen(true); }}>
                                  <UserPlus className="w-4 h-4 mr-2" />
                                  Convert to Employee
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => { setSelectedCandidate(candidate); setDeleteDialogOpen(true); }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Add New Candidate
            </DialogTitle>
            <DialogDescription>Add a new candidate to your talent pool</DialogDescription>
          </DialogHeader>
          {candidateFormContent}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={formLoading} className="bg-purple-600 hover:bg-purple-700">
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Candidate</DialogTitle>
          </DialogHeader>
          {candidateFormContent}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={formLoading} className="bg-purple-600 hover:bg-purple-700">
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCandidate?.name}</DialogTitle>
            <DialogDescription>{selectedCandidate?.email}</DialogDescription>
          </DialogHeader>
          {selectedCandidate && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p>{selectedCandidate.phone || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Current Company</Label>
                  <p>{selectedCandidate.current_company || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Experience</Label>
                  <p>{selectedCandidate.years_of_experience ? `${selectedCandidate.years_of_experience} years` : '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Notice Period</Label>
                  <p>{selectedCandidate.notice_period || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Current CTC</Label>
                  <p>{selectedCandidate.current_ctc ? `₹${selectedCandidate.current_ctc} LPA` : '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Expected CTC</Label>
                  <p>{selectedCandidate.expected_ctc ? `₹${selectedCandidate.expected_ctc} LPA` : '-'}</p>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <Label className="text-muted-foreground">Skills</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedCandidate.skills?.length > 0 ? selectedCandidate.skills.map(s => (
                    <Badge key={s} variant="secondary">{s}</Badge>
                  )) : <span className="text-muted-foreground">-</span>}
                </div>
              </div>

              {selectedCandidate.status_history?.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <Label className="text-muted-foreground">Status History</Label>
                    <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto">
                      {selectedCandidate.status_history.map((entry, idx) => (
                        <div key={idx} className="text-sm p-2 bg-slate-50 rounded">
                          <div className="flex justify-between">
                            <span className="font-medium">{entry.status}</span>
                            <span className="text-muted-foreground">
                              {new Date(entry.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-muted-foreground text-xs">
                            Stage: {entry.stage} • By: {entry.changed_by}
                          </p>
                          {entry.notes && <p className="text-xs mt-1">{entry.notes}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Candidate</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedCandidate?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={formLoading}>
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Employee Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to Employee</DialogTitle>
            <DialogDescription>
              Convert {selectedCandidate?.name} to an employee in the Asset Manager system.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will create a new employee record with the candidate's information. 
              You can then assign assets to them in the Asset Manager.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConvertToEmployee} disabled={formLoading} className="bg-emerald-600 hover:bg-emerald-700">
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Convert to Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Results Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => { if (!bulkProcessing) setUploadDialogOpen(open); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Bulk Upload
              {bulkProcessing && <Loader2 className="w-4 h-4 animate-spin text-purple-600" />}
              {!bulkProcessing && bulkResults.length > 0 && (
                <Badge variant="secondary" className="text-xs font-normal">
                  {bulkResults.filter(r => r.status === 'done').length}/{bulkResults.length} completed
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {bulkResults.map((result, idx) => (
              <div key={idx} className={`p-3 rounded-lg border ${
                result.status === 'done' ? 'bg-green-50 border-green-200' :
                result.status === 'duplicate' ? 'bg-yellow-50 border-yellow-200' :
                result.status === 'error' ? 'bg-red-50 border-red-200' :
                result.status === 'uploading' || result.status === 'creating' ? 'bg-blue-50 border-blue-200' :
                'bg-slate-50 border-slate-200'
              }`} data-testid={`bulk-result-${idx}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {result.status === 'done' && <CheckCircle className="w-4 h-4 text-green-600" />}
                    {result.status === 'duplicate' && <AlertCircle className="w-4 h-4 text-yellow-600" />}
                    {result.status === 'error' && <X className="w-4 h-4 text-red-600" />}
                    {(result.status === 'uploading' || result.status === 'creating') && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
                    {result.status === 'pending' && <Clock className="w-4 h-4 text-slate-400" />}
                    <span className="font-medium text-sm">{result.filename}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-[11px] ${
                      result.status === 'done' ? 'text-green-700 border-green-300' :
                      result.status === 'uploading' ? 'text-blue-700 border-blue-300' :
                      result.status === 'creating' ? 'text-blue-700 border-blue-300' :
                      result.status === 'duplicate' ? 'text-yellow-700 border-yellow-300' :
                      result.status === 'error' ? 'text-red-700 border-red-300' :
                      'text-slate-500 border-slate-300'
                    }`}>
                      {result.status === 'uploading' ? 'Parsing...' :
                       result.status === 'creating' ? 'Creating...' :
                       result.status === 'done' ? 'Created' :
                       result.status === 'duplicate' ? 'Duplicate' :
                       result.status === 'error' ? 'Failed' :
                       'Waiting'}
                    </Badge>
                    {result.candidate_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs gap-1 text-purple-600 hover:text-purple-700"
                        onClick={() => openCandidateNewWindow(result.candidate_id)}
                        data-testid={`open-candidate-${idx}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open
                      </Button>
                    )}
                  </div>
                </div>
                {result.parsed_data && result.status === 'done' && (
                  <div className="mt-1">
                    <p className="text-sm text-green-700">
                      {result.parsed_data.name}{result.parsed_data.email ? ` — ${result.parsed_data.email}` : ''}
                      {result.parsed_data.location ? ` — ${result.parsed_data.location}` : ''}
                    </p>
                    {result.parsed_data.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className="text-[10px] text-slate-500 mr-1">Skills:</span>
                        {result.parsed_data.skills.slice(0, 5).map((skill, si) => (
                          <Badge key={si} variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">{skill}</Badge>
                        ))}
                        {result.parsed_data.skills.length > 5 && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">+{result.parsed_data.skills.length - 5} more</Badge>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {result.error && (
                  <p className="text-sm text-red-600 mt-1">{result.error}</p>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setUploadDialogOpen(false)}
              disabled={bulkProcessing}
            >
              {bulkProcessing ? 'Processing...' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CandidateList;
